import { useCallback } from "react";
import type { TaskItem } from "@edge/shared";
import { createCrmTask, updateCrmTask } from "../api";

export interface CrmTaskCreateInput {
  subject: string;
  description?: string;
  due_date?: string;
  deal_id?: string;
  priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
}

/**
 * CRM task write actions. Each method writes to Zoho via the worker and
 * triggers `reload()` so the cockpit reflects the new state. Errors surface
 * to the caller (the WriteConfirm dialog handles display).
 */
export function useCrmTaskActions(reload: () => void) {
  const complete = useCallback(
    async (task: TaskItem) => {
      await updateCrmTask(task.id, { status: "Completed" });
      reload();
    },
    [reload],
  );

  const reschedule = useCallback(
    async (task: TaskItem, newDate: string) => {
      await updateCrmTask(task.id, { due_date: newDate });
      reload();
    },
    [reload],
  );

  const create = useCallback(
    async (input: CrmTaskCreateInput) => {
      await createCrmTask(input);
      reload();
    },
    [reload],
  );

  /**
   * Edit subject / due_date / priority / status / description on an existing
   * Zoho task. Only sends the fields actually passed — the caller (edit dialog)
   * has already diffed against the original so no-op fields stay out of the PATCH.
   */
  const edit = useCallback(
    async (
      taskId: string,
      fields: {
        subject?: string;
        due_date?: string | null;
        priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
        status?: "Not Started" | "In Progress" | "Completed" | "Waiting on someone else" | "Deferred";
        description?: string;
      },
    ) => {
      await updateCrmTask(taskId, fields);
      reload();
    },
    [reload],
  );

  return { complete, reschedule, create, edit };
}
